import React, { useState, useRef } from "react";
import WebLayout from "../components/WebLayout";
import { toast } from "sonner";
import { CheckCircle2, Sparkles, AlertCircle, Loader2 } from "lucide-react";
import TurnstileWidget from "../components/TurnstileWidget";
import Seo from "../components/Seo";

const PLATFORMS = ["TikTok", "YouTube", "Facebook", "Instagram", "Other"];
const FOLLOWER_RANGES = ["< 1K", "1K–10K", "10K–50K", "50K–250K", "250K–1M", "1M+"];
const NICHES = ["Vending", "Side Hustles", "Trucking", "Gig Work", "Small Business", "Real Estate", "Logistics", "Entrepreneurship", "Other"];
const API = process.env.REACT_APP_BACKEND_URL || "";
function readCookie(name) {
  try { return decodeURIComponent(document.cookie.split("; ").find((c) => c.startsWith(name + "="))?.split("=")[1] || ""); } catch { return ""; }
}

export default function CreatorsLanding() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", platforms: [], handle: "", followers: "", niches: [], notes: "", website_hp: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [ownCode, setOwnCode] = useState("");
  const [ownLink, setOwnLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState("");
  const [tsToken, setTsToken] = useState("");
  const [consent, setConsent] = useState(false);
  const tsRef = useRef(null);
  const tsRequired = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggle = (k, v) => setForm({ ...form, [k]: form[k].includes(v) ? form[k].filter((x) => x !== v) : [...form[k], v] });

  async function handleSubmit(e) {
    e.preventDefault();
    if (tsRequired && !tsToken) {
      setBanner("Please complete the verification challenge and try again.");
      return;
    }
    setLoading(true); setBanner("");
    try {
      const r = await fetch(`${API}/api/preregister/creator`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          platforms: form.platforms, handle: form.handle || null,
          audience_size_bucket: form.followers || null,
          content_niche: form.niches, notes: form.notes || null,
          referral_code: readCookie("sb_ref") || null,
          website_hp: form.website_hp,
          turnstile_token: tsToken,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.status === 200 && data.status === "created") {
        setSubmitted(true);
        setOwnCode(data.own_referral_code || "");
        setOwnLink(data.own_referral_link || "");
        toast.success("Creator early access received.");
      }
      else if (r.status === 200 && data.status === "already_registered") { setSubmitted(true); setAlreadyRegistered(true); toast.success("Already on the list."); }
      else if (r.status === 422 && data.status === "turnstile_failed") { setBanner("Verification failed. Please try again."); setTsToken(""); tsRef.current?.reset(); }
      else if (r.status === 422) { setBanner("Please review the form and try again."); setTsToken(""); tsRef.current?.reset(); }
      else if (r.status === 429) { setBanner("Too many attempts. Please try again later."); setTsToken(""); tsRef.current?.reset(); }
      else { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    } catch { setBanner("Something went wrong. Please try again."); setTsToken(""); tsRef.current?.reset(); }
    finally { setLoading(false); }
  }

  return (
    <WebLayout>
      <Seo title="For Creators" description="Turn your audience into earnings — join the SpotBounty creator program early access." path="/creators" />
      <section className="max-w-7xl mx-auto px-6 py-16 grid lg:grid-cols-[1.1fr_1fr] gap-14">
        <div>
          <div className="sb-eyebrow flex items-center gap-2"><Sparkles size={14} className="text-[#FFC107]" /> For Creators</div>
          <h1 className="sb-headline mt-5 text-[48px] md:text-[64px]">
            TURN YOUR AUDIENCE <br /> INTO <span className="text-[#FFC107]">EARNINGS.</span>
          </h1>
          <p className="mt-5 text-[#A1A1AA] max-w-lg leading-relaxed">
            You cover side hustles, entrepreneurship, vending, trucking, or real estate. We give you a unique link,
            content kits, and recurring commissions for every scout or operator you refer.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-4">
            <div className="sb-card p-5">
              <div className="text-[26px] font-black">Recurring</div>
              <div className="sb-eyebrow mt-1">Commissions</div>
              <p className="mt-2 text-[13px] text-[#A1A1AA]">Earn every time a referred operator purchases a location.</p>
            </div>
            <div className="sb-card p-5">
              <div className="text-[26px] font-black">Content Kit</div>
              <div className="sb-eyebrow mt-1">Ready-to-post</div>
              <p className="mt-2 text-[13px] text-[#A1A1AA]">Scripts, graphics, QR posters and platform-native captions.</p>
            </div>
          </div>
        </div>

        <div>
          {!submitted ? (
            <form
              data-testid="creator-form"
              onSubmit={handleSubmit}
              className="sb-card p-6 md:p-8 space-y-4"
            >
              <div>
                <div className="sb-eyebrow">Creator Program</div>
                <h2 className="text-[28px] font-black mt-2">Join creator early access.</h2>
              </div>
              {banner && (
                <div className="sb-card p-3 border-[#EF4444]/40 flex items-start gap-2" style={{ background: "rgba(239,68,68,.06)" }}>
                  <AlertCircle size={14} className="text-[#EF4444] mt-0.5" />
                  <span className="text-[12.5px] text-[#EF4444]">{banner}</span>
                </div>
              )}
              <div><label className="sb-label">Creator Name</label><input required data-testid="cr-name" className="sb-input" value={form.name} onChange={set("name")} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="sb-label">Email</label><input required type="email" data-testid="cr-email" className="sb-input" value={form.email} onChange={set("email")} /></div>
                <div><label className="sb-label">Mobile Phone</label><input required data-testid="cr-phone" className="sb-input" value={form.phone} onChange={set("phone")} /></div>
              </div>
              <div>
                <label className="sb-label">Platforms</label>
                <div className="flex flex-wrap gap-2">
                  {PLATFORMS.map((p) => (
                    <button key={p} type="button" onClick={() => toggle("platforms", p)} className={`sb-pill ${form.platforms.includes(p) ? "sb-pill-gold" : "sb-pill-gray"}`}>{p}</button>
                  ))}
                </div>
              </div>
              <div><label className="sb-label">Handle / Channel URL</label><input data-testid="cr-handle" className="sb-input" value={form.handle} onChange={set("handle")} placeholder="@jordan or https://tiktok.com/@jordan" /></div>
              <div>
                <label className="sb-label">Approximate Followers</label>
                <select data-testid="cr-followers" className="sb-input" value={form.followers} onChange={set("followers")}>
                  <option value="">Select…</option>
                  {FOLLOWER_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="sb-label">Content Niche</label>
                <div className="flex flex-wrap gap-2">
                  {NICHES.map((p) => (
                    <button key={p} type="button" onClick={() => toggle("niches", p)} className={`sb-pill ${form.niches.includes(p) ? "sb-pill-gold" : "sb-pill-gray"}`}>{p}</button>
                  ))}
                </div>
              </div>
              <div><label className="sb-label">Notes</label><textarea data-testid="cr-notes" className="sb-input" rows={3} value={form.notes} onChange={set("notes")} /></div>
              <TurnstileWidget ref={tsRef} onToken={setTsToken} testId="cr-turnstile" />
              <label htmlFor="cr-consent" className="flex items-start gap-2 text-[12px] text-[#A1A1AA] leading-relaxed cursor-pointer">
                <input id="cr-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="cr-consent" className="mt-[3px] w-4 h-4 accent-[#FFC107] shrink-0" required />
                <span>I agree to the <a href="/terms" className="text-[#FFC107] underline">Terms of Service</a> and acknowledge the <a href="/privacy" className="text-[#FFC107] underline">Privacy Policy</a>.</span>
              </label>
              <button type="submit" disabled={loading || (tsRequired && !tsToken) || !consent} data-testid="cr-submit" className="sb-btn-primary w-full disabled:opacity-50">
                {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Join Creator Early Access"}
              </button>
              <input type="text" name="website_hp" value={form.website_hp} onChange={set("website_hp")} tabIndex={-1} autoComplete="off" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }} />
            </form>
          ) : (
            <div data-testid="cr-confirmation" className="sb-card p-10 text-center">
              <CheckCircle2 size={48} className="text-[#FFC107] mx-auto" />
              <div className="mt-5 sb-eyebrow">Confirmation</div>
              <h2 className="text-[32px] font-black mt-3">WELCOME TO THE CREW.</h2>
              <p className="mt-4 text-[#A1A1AA] max-w-md mx-auto">
                {alreadyRegistered
                  ? "You're already on the list — thanks for reconfirming your interest."
                  : "We'll reach out with your unique creator link and content kit."}
              </p>
            </div>
          )}
        </div>
      </section>
    </WebLayout>
  );
}
